import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { PracticeInfo, Slot } from '../api/bookingApi';

interface ConfirmationStepProps {
  practiceInfo: PracticeInfo | null;
  apptType: string;
  slot: Slot | null;
  patientName: string;
  onReset: () => void;
}

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function formatTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function buildICS({ practiceName, apptType, startIso, durationMinutes = 60, address }: {
  practiceName: string;
  apptType: string;
  startIso: string;
  durationMinutes?: number;
  address?: string;
}): string {
  const start = new Date(startIso);
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);

  function toICSDate(d: Date): string {
    return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Operaitor//Booking//EN',
    'BEGIN:VEVENT',
    `UID:${Date.now()}@operaitor.co`,
    `DTSTAMP:${toICSDate(new Date())}`,
    `DTSTART:${toICSDate(start)}`,
    `DTEND:${toICSDate(end)}`,
    `SUMMARY:${apptType} – ${practiceName}`,
    `DESCRIPTION:Your appointment at ${practiceName}`,
    ...(address ? [`LOCATION:${address}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.join('\r\n');
}

export default function ConfirmationStep({
  practiceInfo,
  apptType,
  slot,
  patientName,
  onReset,
}: ConfirmationStepProps) {
  const startIso = slot?.datetime_iso || slot?.time || '';
  const practiceName = practiceInfo?.name || 'the practice';
  const practiceAddress = practiceInfo?.address || '';

  useEffect(() => {
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    const interval: any = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  function downloadICS() {
    const content = buildICS({ practiceName, apptType, startIso, address: practiceAddress });
    const blob = new Blob([content], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'appointment.ics';
    a.click();
    URL.revokeObjectURL(url);
  }

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: "spring" as const, stiffness: 100 } }
  };

  return (
    <motion.div className="relative pt-12 w-full" variants={container} initial="hidden" animate="show">
      {/* Billion Dollar Success Badge */}
      <motion.div 
        className="absolute -top-24 inset-x-0 flex justify-center z-30"
        variants={item}
      >
        <div className="relative">
          {/* Animated Success Rings (Shockwaves) */}
          {[1, 2].map((i) => (
            <motion.div
              key={i}
              className="absolute inset-0 bg-[#10b981] rounded-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 2.2, opacity: 0 }}
              transition={{ 
                duration: 1.5, 
                delay: 0.5 + (i * 0.3), 
                ease: "easeOut",
                repeat: 2 
              }}
            />
          ))}

          {/* Premium Glow Aura */}
          <motion.div 
            className="absolute inset-[-20px] bg-gradient-to-tr from-[#10b981]/40 to-[#34d399]/10 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Main Badge Body */}
          <motion.div 
            className="relative bg-white rounded-full shadow-[0_20px_50px_rgba(0,0,0,0.15),inset_0_-4px_10px_rgba(16,185,129,0.05)] border-[6px] border-white w-20 h-20 sm:w-28 sm:h-28 flex items-center justify-center"
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
          >
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16"
              viewBox="0 0 100 100"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient id="checkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              
              {/* Animated Outer Circle Rim */}
              <motion.circle
                cx="50"
                cy="50"
                r="44"
                stroke="url(#checkGradient)"
                strokeWidth="8"
                strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1, ease: "easeInOut", delay: 0.4 }}
              />

              {/* High-End Elastic Checkmark */}
              <motion.path
                d="M30 52L44 66L72 38"
                stroke="url(#checkGradient)"
                strokeWidth="12"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.9, 
                  ease: [0.16, 1, 0.3, 1] // Custom cubic-bezier for a "premium" feel
                }}
              />
            </svg>

            {/* Subtle Inner Highlight */}
            <div className="absolute inset-2 rounded-full border border-[#10b981]/5 pointer-events-none" />
          </motion.div>
        </div>
      </motion.div>

      {/* Content integrated directly into the Shell's card */}
      <div className="text-center mb-8">
        <motion.h2 
          className="text-2xl sm:text-4xl font-black text-gray-900 tracking-tighter leading-tight"
          variants={item}
        >
          You're booked!
        </motion.h2>
        <motion.p 
          className="text-gray-500 font-bold text-sm mt-2 uppercase tracking-[0.2em]"
          variants={item}
        >
          Confirmed Appointment
        </motion.p>
      </div>
      
      <motion.div 
        className="space-y-6 py-8 border-y border-dashed border-gray-200 relative"
        variants={item}
      >
        <Detail icon="building" label="Practice" value={practiceName} />
        <Detail icon="clipboard" label="Appointment" value={apptType} />
        {startIso && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 pt-2">
            <Detail icon="calendar" label="Date" value={formatDate(startIso)} />
            <Detail icon="clock" label="Time" value={formatTime(startIso)} />
          </div>
        )}
      </motion.div>

      <motion.div className="pt-8 space-y-4" variants={item}>
        <p className="text-gray-500 font-medium italic text-base sm:text-lg text-center px-4 leading-relaxed">
          {`We look forward to having you${patientName ? `, ${patientName.split(' ')[0]}` : ''}!`}
        </p>
        {/* Forms are not collected here any more (operaitor/backend#1604 slice 1): when a
            visit needs them, appointment-prep texts the patient a link. This screen is terminal. */}
        <p className="text-gray-500 text-sm mb-6 text-center px-4 leading-relaxed">
          If your visit requires any forms, we'll text you a link before your appointment.
        </p>

        {startIso && (
          <button onClick={downloadICS} className="btn-secondary !h-12 sm:!h-14">
            Add to Apple / Google Calendar
          </button>
        )}
        <button onClick={onReset} className="btn-primary !h-12 sm:!h-14">
          Book Another Appointment
        </button>
      </motion.div>
    </motion.div>
  );
}

type IconKey = 'building' | 'clipboard' | 'calendar' | 'clock';

function Detail({ icon, label, value }: { icon: IconKey; label: string; value: string }) {
  const icons: Record<IconKey, string> = {
    building: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 00-1-1h-2a1 1 0 00-1 1v5m4 0H9',
    clipboard: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
    calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2v12a2 2 0 002 2z',
    clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  };

  return (
    <div className="flex items-start gap-3 text-left">
      <div className="w-6 h-6 mt-1 text-primary/60 flex-shrink-0">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d={icons[icon]} />
        </svg>
      </div>
      <div>
        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.1em]">{label}</p>
        <p className="text-gray-900 font-bold text-lg leading-tight">{value}</p>
      </div>
    </div>
  );
}
